import { Link } from "@tanstack/react-router";
import { Topbar } from "./Topbar";
import { Footer } from "./Footer";
import { Hero } from "./Hero";
import { Section } from "./Section";
import { CTABlock } from "./CTABlock";
import { RevealOnScroll } from "./RevealOnScroll";
import { REGIONS, type RegionSlug } from "@/lib/regions-data";
import { pathMap } from "@/lib/locale";

type Props = {
  slug: RegionSlug;
  locale: "de" | "en";
};

export function RegionPage({ slug, locale }: Props) {
  const r = REGIONS[slug];
  const isEn = locale === "en";
  const contactPath = isEn ? pathMap.contact.en : pathMap.contact.de;
  const overviewPath = isEn ? "/en/regions" : "/regionen";
  const startPath = isEn ? "/en" : "/";

  const tr = {
    home: isEn ? "Start" : "Start",
    regions: isEn ? "Regions" : "Regionen",
    standpoint: isEn ? "Our standpoint" : "Unser Standpunkt",
    differentiators: isEn ? "What's different here" : "Was hier anders ist",
    services: isEn ? "Relevant services" : "Relevante Leistungen",
    logistics: isEn ? "How we work with you" : "Wie wir mit Ihnen arbeiten",
    other: isEn ? "Other regions" : "Weitere Regionen",
    ctaPrimary: isEn ? "Start a conversation" : "Erst-Gespräch anfragen",
    ctaSecondary: isEn ? "All regions" : "Alle Regionen",
    economyKicker: r.economy.eyebrow[locale],
    standpointHead: isEn ? "Why this region matters to us" : "Warum diese Region für uns relevant ist",
    differentiatorsKicker: isEn ? "Differentiators" : "Differenzierer",
    servicesKicker: isEn ? "Disciplines" : "Disziplinen",
    logisticsKicker: isEn ? "Operations" : "Ablauf",
    relevance: isEn ? "Why relevant" : "Warum relevant",
    learnMore: isEn ? "Learn more ↗" : "Mehr erfahren ↗",
  };

  const otherRegions = (Object.keys(REGIONS) as RegionSlug[]).filter((s) => s !== slug);
  const contactWithTopic = `${contactPath}?topic=${r.topicSlug}`;
  const hasExpectations = !!r.expectations;
  const n = (base: number) => String(base + (hasExpectations ? 1 : 0)).padStart(2, "0");


  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow={r.hero.eyebrow[locale]}
          headline={
            <>
              {r.hero.headlineEm[locale][0]}
              <em>{r.hero.headlineEm[locale][1]}</em>
            </>
          }
          lead={r.hero.lead[locale]}
          metaStrip={[...r.metaStrip[locale]]}
          breadcrumbs={[
            { label: tr.home, to: startPath },
            { label: tr.regions, to: overviewPath },
            { label: r.cityName },
          ]}
        />

        <Section
          number="02"
          kicker={tr.standpoint}
          headline={
            <>
              {tr.standpointHead}.{" "}
              <em className="whitespace-nowrap [overflow-wrap:normal] [hyphens:none]">{r.cityName}.</em>
            </>
          }
        >
          <div className="max-w-3xl space-y-6 text-lg leading-relaxed text-ink-2">
            {r.standpoint[locale].map((p, i) => (
              <RevealOnScroll key={i} delay={i * 80}>
                <p>{p}</p>
              </RevealOnScroll>
            ))}
          </div>
        </Section>

        <Section
          number="03"
          variant="tinted"
          kicker={tr.economyKicker}
          headline={<em>{r.economy.headline[locale]}</em>}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {r.economy.cards.map((card, i) => (
              <RevealOnScroll key={i} delay={i * 60}>
                <div className="h-full rounded-lg border border-rule bg-white p-7">
                  <h3 className="font-display text-xl leading-tight text-ink">
                    {card.title[locale]}
                  </h3>
                  <p className="mt-4 leading-relaxed text-ink-3">
                    {card.body[locale]}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </Section>

        <Section
          number="04"
          kicker={tr.differentiatorsKicker}
          headline={<em>{tr.differentiators}.</em>}
        >
          <ul className="divide-y divide-rule border-t border-b border-rule">
            {r.differentiators[locale].map((d, i) => (
              <li key={i} className="grid gap-4 py-6 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                <div className="font-display text-lg leading-snug text-ink">
                  {d.lead}
                </div>
                <div className="leading-relaxed text-ink-3">{d.body}</div>
              </li>
            ))}
          </ul>
        </Section>

        {r.expectations && (
          <Section
            number="05"
            variant="tinted"
            kicker={r.expectations.eyebrow[locale]}
            headline={<em>{r.expectations.headline[locale]}</em>}
          >
            <div className="max-w-3xl">
              <RevealOnScroll>
                <p className="text-lg leading-relaxed text-ink-2">
                  {r.expectations.intro[locale]}
                </p>
              </RevealOnScroll>
              <div className="mt-10 space-y-8">
                {r.expectations.blocks[locale].map((b, i) => (
                  <RevealOnScroll key={i} delay={i * 60}>
                    <h3 className="font-display text-xl leading-snug text-ink">
                      {b.title}
                    </h3>
                    <p className="mt-3 leading-relaxed text-ink-3">{b.body}</p>
                  </RevealOnScroll>
                ))}
              </div>
            </div>
          </Section>
        )}

        <Section
          number={n(5)}
          variant={hasExpectations ? undefined : "tinted"}
          kicker={tr.servicesKicker}
          headline={<em>{tr.services}.</em>}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {r.services
              .filter((s) => !(isEn && s.id === "seo-wuerzburg"))
              .map((s, i) => {
                const isFeatured = !isEn && s.id === "seo-wuerzburg";
                return (
                  <RevealOnScroll key={s.id} delay={i * 50}>
                    <Link
                      to={isEn ? s.hrefEn : s.hrefDe}
                      className={`group block h-full rounded-lg border bg-white p-6 transition-colors ${
                        isFeatured
                          ? "md:col-span-2 border-cyan-deep/40 bg-cyan-tint/40 hover:border-cyan-deep"
                          : "border-rule hover:border-cyan-deep/50"
                      }`}
                    >
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-deep">
                        {isFeatured ? (isEn ? "Featured · Region" : "Empfohlen · Region") : tr.relevance}
                      </div>
                      <div className={`mt-2 font-display leading-snug text-ink ${isFeatured ? "text-2xl" : "text-lg"}`}>
                        {s.label[locale]}
                      </div>
                      <p className={`mt-3 leading-relaxed text-ink-2 ${isFeatured ? "text-[17px] max-w-2xl" : ""}`}>
                        {s.relevance[locale]}
                      </p>
                      <div className="mt-4 font-mono text-xs uppercase tracking-wider text-cyan-deep group-hover:underline">
                        {tr.learnMore}
                      </div>
                    </Link>
                  </RevealOnScroll>
                );
              })}
          </div>
        </Section>

        <Section
          number={n(6)}
          kicker={tr.logisticsKicker}
          headline={<em>{tr.logistics}.</em>}
          caption={r.logistics.intro[locale]}
        >
          <div className="overflow-hidden rounded-lg border border-rule">
            {r.logistics.rows[locale].map((row, i) => (
              <div
                key={i}
                className={`grid gap-4 px-6 py-5 md:grid-cols-[180px_minmax(0,1fr)] ${
                  i % 2 === 0 ? "bg-white" : "bg-bg-subtle"
                }`}
              >
                <div className="font-mono text-xs uppercase tracking-wider text-ink-4 md:pt-1">
                  {row.label}
                </div>
                <div className="leading-relaxed text-ink-2">{row.body}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section number={n(7)} variant="deep">

          <CTABlock
            variant="deep"
            headline={<em>{r.cta.headline[locale]}</em>}
            body={r.cta.body[locale]}
            primary={{ label: tr.ctaPrimary, to: contactWithTopic }}
            secondary={{ label: tr.ctaSecondary, to: overviewPath }}
          />
        </Section>

        <Section number={n(8)} variant="tinted" kicker={tr.other} headline={<em>{tr.other}.</em>}>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {otherRegions.map((s) => {
              const o = REGIONS[s];
              const href = isEn ? `/en/regions/${s}` : `/regionen/${s}`;
              return (
                <Link
                  key={s}
                  to={href}
                  className="group block rounded-lg border border-rule bg-white p-5 transition-colors hover:border-cyan-deep/50"
                >
                  <div className="font-display text-lg leading-tight text-ink">
                    {o.cityName}
                  </div>
                  <div className="mt-2 font-mono text-[11px] uppercase tracking-wider text-ink-4">
                    {o.distanceKm} km · {o.driveMinutes} min
                  </div>
                </Link>
              );
            })}
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
